import type { ReactNode } from 'react'

export type ChipTone = 'neutral' | 'ok' | 'warning' | 'critical' | 'off'

export interface RowChip {
  key: string
  text: string
  tone?: ChipTone
}

interface RowProps {
  /** What this is called in the UI. */
  name: string
  /** The underlying GA4 name, when it differs from the label — shown as code. */
  code?: string
  chips?: RowChip[]
  /** One line of prose. Truncated to one line: the drawer holds the detail. */
  summary?: ReactNode
  /** Dimmed rather than hidden — a disabled item is still configuration. */
  dimmed?: boolean
  /** Marks the row as the one whose drawer is open. */
  active?: boolean
  onOpen: () => void
}

export function ConfigList({ children }: { children: ReactNode }) {
  return <div className="config-list">{children}</div>
}

/**
 * One line per configured thing: enough to answer "what is set up here" without
 * opening anything, and a click to edit it.
 *
 * The whole row is the button. A row with its own buttons inside would nest
 * interactive controls, and the two targets would compete for the same click — so
 * remove, enable and every field live in the drawer instead.
 */
export function ConfigRow({ name, code, chips, summary, dimmed, active, onOpen }: RowProps) {
  return (
    <button
      type="button"
      className={
        'config-row' +
        (dimmed ? ' config-row--dimmed' : '') +
        (active ? ' config-row--active' : '')
      }
      onClick={onOpen}
    >
      <span className="config-row__main">
        <span className="config-row__head">
          <span className="config-row__name">{name}</span>
          {code && code !== name && <code className="config-row__code">{code}</code>}
        </span>
        {summary && <span className="config-row__summary">{summary}</span>}
      </span>
      {chips && chips.length > 0 && (
        <span className="config-row__chips">
          {chips.map((chip) => (
            <span key={chip.key} className={`chip chip--${chip.tone ?? 'neutral'}`}>
              {chip.text}
            </span>
          ))}
        </span>
      )}
      <span className="config-row__go" aria-hidden="true">
        &rsaquo;
      </span>
    </button>
  )
}
